import { fold } from './brief';

/**
 * A product the house never offers, decided by its title — one rule for every
 * door that offers or writes products (the agent's search and commit, day
 * Suggestions, the Mini Planner, the planner). House `adaptadores.Mundo._prod`
 * (`proibido`) @v7.13.2: a row marked internal or not to use, another
 * agency's product (Audley), or a template.
 *
 * The title read is the house's (`houseTitle`): the English one, else the
 * first with text, else `title_en` — as v7's `campos.titulo`. A mark left only
 * in a translation does not count (owner decision 2026-10-07: TempoVIP's
 * catalogue has hotels marked in FR/DE titles alone that the house still
 * sells). One divergence, stricter: an Audley lead gets no Audley products
 * either (owner decision 2026-10-07; v7's `lead_audley` lifts it).
 *
 * The same patterns exist for SQL as `FORBIDDEN_TITLE_SQL`, run over
 * `unaccent(houseTitleSql(...))` (`@travel-planner/db/sql`) so they agree with
 * the folded text the JS form reads.
 *
 * Pure.
 */

const DO_NOT_USE = String.raw`duplicate|nao\s+usar|do\s+not\s+use|site\s*inspection|fam\s*trip`;
const AUDLEY = 'audley';
const TEMPLATE = 'template';

const DO_NOT_USE_RE = new RegExp(String.raw`\b(?:${DO_NOT_USE})\b`, 'i');
const AUDLEY_RE = new RegExp(String.raw`\b${AUDLEY}\b`, 'i');
const TEMPLATE_RE = new RegExp(String.raw`\b${TEMPLATE}\b`, 'i');

/**
 * The three patterns as one POSIX regular expression (`\y` is Postgres' word
 * boundary), for `unaccent(<text>) ~* FORBIDDEN_TITLE_SQL`.
 */
export const FORBIDDEN_TITLE_SQL = String.raw`\y(${DO_NOT_USE}|${AUDLEY}|${TEMPLATE})\y`;

export type ForbiddenProductInput = {
  /** The house's title (`houseTitle`). */
  readonly title: string | null | undefined;
  /** The catalogue it comes from: Audley's own catalogue is Audley's. */
  readonly catalogueName?: string | null;
};

/**
 * The title the house reads (`campos.titulo` @v7.13.2): the English entry of
 * an i18n array, else the first entry with text; `{en: …}` or a bare string as
 * stored; else `title_en`.
 */
export const houseTitle = (title: unknown, titleEn?: string | null): string => {
  if (typeof title === 'string' && title) return title;
  if (Array.isArray(title)) {
    const entries = title.filter(
      (e): e is { languageCode?: unknown; text: string } =>
        !!e && typeof e === 'object' && typeof e.text === 'string' && !!e.text
    );
    const en = entries.find((e) => e.languageCode === 'en');
    if (en ?? entries[0]) return (en ?? entries[0])?.text ?? '';
  } else if (title && typeof title === 'object') {
    const o = title as Record<string, unknown>;
    const first =
      typeof o.en === 'string' && o.en
        ? o.en
        : Object.values(o).find((v) => typeof v === 'string' && v);
    if (typeof first === 'string') return first;
  }
  return titleEn ?? '';
};

/** Why the house does not offer this product, or null when it does. */
export const forbiddenReason = ({
  title,
  catalogueName,
}: ForbiddenProductInput): string | null => {
  const t = fold(title ?? '');
  const m = DO_NOT_USE_RE.exec(t);
  if (m) return `marked internal / do not use (${m[0]})`;
  if (AUDLEY_RE.test(t) || fold(catalogueName ?? '').trim() === AUDLEY)
    return "another agency's product (Audley)";
  if (TEMPLATE_RE.test(t))
    return 'a template, not a real place: pick a real catalogue product';
  return null;
};

/** `forbiddenReason` over one title, for callers holding a single string. */
export const isForbiddenTitle = (title: string): boolean =>
  forbiddenReason({ title }) != null;
